import React, { useState, useEffect, useCallback } from 'react';
import { AfterHoursOverride } from '../../hooks/useAfterHoursOverrides';
import { API_URL } from '../../constants/appConstants';
import './AfterHoursOverridePanel.css';

interface AfterHoursOverridePanelProps {
  overrides: AfterHoursOverride[];
  loading: boolean;
  onAdd: (data: {
    employeeId: string;
    employeeName: string;
    date: string;
    reason?: string;
  }) => Promise<{ success: boolean; message?: string }>;
  onRemove: (overrideId: string) => Promise<{ success: boolean; message?: string }>;
  onRefresh: () => void;
}

const AfterHoursOverridePanel: React.FC<AfterHoursOverridePanelProps> = ({
  overrides,
  loading,
  onAdd,
  onRemove,
  onRefresh
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(true);

  const [developers, setDevelopers] = useState<{ id: string; name: string }[]>([]);
  const [devsLoading, setDevsLoading] = useState(false);

  const fetchDevelopers = useCallback(async () => {
    setDevsLoading(true);
    try {
      const response = await fetch(`${API_URL}/intranet/developers`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setDevelopers(
          (data || [])
            .map((d: any) => ({ id: String(d.id), name: String(d.name) }))
            .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name))
        );
      }
    } catch (err) {
      console.error('Failed to fetch developers:', err);
    } finally {
      setDevsLoading(false);
    }
  }, []);

  useEffect(function loadDevsWhenFormOpens() {
    if (isFormOpen && developers.length === 0) {
      fetchDevelopers();
    }
  }, [isFormOpen, developers.length, fetchDevelopers]);

  const resetForm = () => {
    setSelectedEmployee('');
    setDate('');
    setReason('');
    setError(null);
  };

  const handleAdd = async () => {
    const dev = developers.find(d => d.id === selectedEmployee);
    if (!dev || !date) {
      setError('Please select an employee and date');
      return;
    }

    setSubmitting(true);
    setError(null);

    const result = await onAdd({
      employeeId: dev.id,
      employeeName: dev.name,
      date,
      reason: reason.trim() || undefined
    });

    setSubmitting(false);

    if (result.success) {
      resetForm();
      setIsFormOpen(false);
    } else {
      setError(result.message || 'Failed to add override');
    }
  };

  const handleRemove = async (overrideId: string) => {
    setRemovingId(overrideId);
    setError(null);

    const result = await onRemove(overrideId);

    setRemovingId(null);

    if (!result.success) {
      setError(result.message || 'Failed to remove override');
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T12:00:00');
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const toLocalDateStr = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTomorrowStr = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toLocalDateStr(d);
  };

  const getTodayStr = () => toLocalDateStr(new Date());

  return (
    <div className="after-hours-panel">
      <div className="panel-header" onClick={() => setCollapsed(!collapsed)}>
        <div className="header-left">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" />
          </svg>
          <h3>After-Hours Overrides</h3>
          {overrides.length > 0 && (
            <span className="count-badge">{overrides.length}</span>
          )}
          <svg
            className={`chevron ${collapsed ? '' : 'open'}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        <button
          className="refresh-btn"
          onClick={(e) => {
            e.stopPropagation();
            onRefresh();
          }}
          disabled={loading}
          title="Refresh"
        >
          {loading ? (
            <div className="btn-spinner" />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          )}
        </button>
      </div>

      {!collapsed && (
        <div className="panel-body">
          {error && (
            <div className="panel-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {overrides.length > 0 ? (
            <div className="overrides-list">
              {overrides.map((override) => (
                <div key={override.id} className="override-card">
                  <div className="override-info">
                    <div className="override-employee">
                      <span className="employee-avatar">
                        {override.employeeName.charAt(0)}
                      </span>
                      <span className="employee-name">{override.employeeName}</span>
                      <span className="employee-id">ID: {override.employeeId}</span>
                    </div>
                    <div className="override-date">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      {formatDate(override.date)}
                    </div>
                    {override.reason && (
                      <div className="override-reason">{override.reason}</div>
                    )}
                  </div>
                  <button
                    className="remove-btn"
                    onClick={() => handleRemove(override.id)}
                    disabled={removingId === override.id}
                    title="Remove override"
                  >
                    {removingId === override.id ? (
                      <div className="btn-spinner" />
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-overrides">No active after-hours overrides</div>
          )}

          {isFormOpen ? (
            <div className="add-form">
              <div className="form-row">
                <div className="form-field flex-grow">
                  <label>Employee</label>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    disabled={devsLoading}
                  >
                    <option value="">
                      {devsLoading ? 'Loading devs...' : '-- Select Employee --'}
                    </option>
                    {developers.map((dev) => (
                      <option key={dev.id} value={dev.id}>
                        {dev.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    min={getTodayStr()}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field flex-grow">
                  <label>Reason (optional)</label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Deployment scheduled for 8 PM"
                  />
                </div>
              </div>
              <div className="form-actions">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    resetForm();
                    setIsFormOpen(false);
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="confirm-btn"
                  onClick={handleAdd}
                  disabled={submitting || !selectedEmployee || !date}
                >
                  {submitting ? <div className="btn-spinner" /> : 'Grant Override'}
                </button>
              </div>
              <div className="form-quick-date">
                <span>Quick set:</span>
                <button type="button" onClick={() => setDate(getTodayStr())}>Today</button>
                <button type="button" onClick={() => setDate(getTomorrowStr())}>Tomorrow</button>
              </div>
            </div>
          ) : (
            <button className="add-override-btn" onClick={() => setIsFormOpen(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Grant After-Hours Access
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AfterHoursOverridePanel;
